<script setup>
import { ref, computed, onMounted } from 'vue';
import DustessPushLogAPI from 'dashboard/api/dustessPushLogs';

const loading = ref(false);
const error = ref('');
const page = ref(1);
const meta = ref({ count: 0, current_page: 1 });
const logs = ref([]);

const stripPhone = p => (p || '').replace(/^\+86[-\s]?/, '') || '-';
const fmtTime = ts => {
  if (!ts) return '-';
  try {
    return new Date(ts).toLocaleString('zh-CN', {
      hour12: false,
      timeZone: 'Asia/Shanghai',
    });
  } catch {
    return ts;
  }
};

const hasMore = computed(() => logs.value.length < meta.value.count);

const fetchLogs = async p => {
  loading.value = true;
  error.value = '';
  try {
    const res = await DustessPushLogAPI.get(p);
    logs.value = res.data?.payload || [];
    meta.value = res.data?.meta || { count: 0, current_page: p };
    page.value = p;
  } catch (e) {
    error.value = e?.response?.data?.error || e?.message || '加载失败';
  } finally {
    loading.value = false;
  }
};

onMounted(() => fetchLogs(1));

const prev = () => page.value > 1 && fetchLogs(page.value - 1);
const next = () => hasMore.value && fetchLogs(page.value + 1);
</script>

<template>
  <div class="flex flex-col gap-4 p-6 h-full overflow-y-auto">
    <div class="flex items-center justify-between">
      <div class="flex flex-col">
        <h2 class="text-lg font-semibold text-n-slate-12">名片推送日志</h2>
        <span class="text-xs text-n-slate-10">共 {{ meta.count }} 条 · 第 {{ page }} 页</span>
      </div>
      <button
        type="button"
        class="h-8 px-3 rounded-md text-sm border border-n-weak text-n-slate-11 hover:bg-n-alpha-2"
        :disabled="loading"
        @click="fetchLogs(page)"
      >
        刷新
      </button>
    </div>

    <div v-if="loading && !logs.length" class="py-20 text-center text-sm text-n-slate-10">
      加载中...
    </div>
    <div v-else-if="error" class="py-20 text-center text-sm text-n-amber-12">
      {{ error }}
    </div>
    <div
      v-else-if="!logs.length"
      class="flex flex-col items-center justify-center py-20 text-sm text-n-slate-10"
    >
      <span class="i-lucide-file-text size-10 mb-3 opacity-60"></span>
      暂无推送日志
    </div>

    <div v-else class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead class="text-n-slate-10">
          <tr class="border-b border-n-weak">
            <th class="py-2 px-2 text-left font-normal">时间</th>
            <th class="py-2 px-2 text-left font-normal">联系人</th>
            <th class="py-2 px-2 text-left font-normal">电话</th>
            <th class="py-2 px-2 text-left font-normal">状态</th>
            <th class="py-2 px-2 text-left font-normal">返回信息</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="l in logs"
            :key="l.id"
            class="border-b border-n-weak/60 text-n-slate-12"
          >
            <td class="py-2 px-2 text-n-slate-10">{{ fmtTime(l.pushed_at || l.created_at) }}</td>
            <td class="py-2 px-2">{{ l.contact?.name || '-' }}</td>
            <td class="py-2 px-2">{{ stripPhone(l.contact?.phone_number) }}</td>
            <td class="py-2 px-2">
              <span :class="l.status === 'ok' ? 'text-green-600' : 'text-red-500'">
                {{ l.status === 'ok' ? '成功' : '失败' }}
              </span>
            </td>
            <td class="py-2 px-2 text-n-slate-10">{{ l.message }}</td>
          </tr>
        </tbody>
      </table>

      <div class="flex items-center justify-end gap-2 pt-2">
        <button
          type="button"
          class="h-8 px-3 rounded-md text-sm border border-n-weak text-n-slate-11 disabled:opacity-50"
          :disabled="page <= 1 || loading"
          @click="prev"
        >
          上一页
        </button>
        <span class="text-sm text-n-slate-11">第 {{ page }} 页</span>
        <button
          type="button"
          class="h-8 px-3 rounded-md text-sm border border-n-weak text-n-slate-11 disabled:opacity-50"
          :disabled="!hasMore || loading"
          @click="next"
        >
          下一页
        </button>
      </div>
    </div>
  </div>
</template>
